import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSimulation } from '../context/SimulationContext';
import { 
  NODE_TABLE, 
  getNode, 
  getChoices, 
  checkIntentMismatch,
  getCharacterImage,
  CHARACTER_IMAGES,
  STAGES,
  TAGS,
} from '../data/engineeringNodeTable';
import { FixedTopBar } from './HUDMeters';
import { PrepCardSummary } from './PrepCardBuilder';
import { CoachNudge, CoachNudgeHistory } from './CoachNudge';
import { MessageCircle, User, AlertTriangle, ArrowRight } from 'lucide-react';

const STAGE_LABELS = {
  [STAGES.ENTRY]: 'Entry',
  [STAGES.OPENING]: 'Opening Move',
  [STAGES.FACT_FINDING]: 'Fact-Finding',
  [STAGES.NAMING]: 'Naming the Issue',
  [STAGES.RESOLUTION]: 'Resolution Path',
  [STAGES.CLOSURE]: 'Closure',
};

export function Simulation() {
  const { 
    state, 
    makeChoice, 
    setPreviewMeters, 
    clearPreview, 
    addCoachNudge,
    dismissNudge,
    recordMismatch,
    calculateOutcome,
    updateCharacterState,
  } = useSimulation();
  
  const { simulation, prepCard, meters } = state;
  const [showResponse, setShowResponse] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);

  const [portraitLoaded, setPortraitLoaded] = useState(false);

  // Preload all character portraits on mount
  useEffect(() => {
    Object.values(CHARACTER_IMAGES).forEach(url => {
      const img = new Image();
      img.src = url;
    });
  }, []);

  // Reset loaded state when character changes
  const handlePortraitLoad = useCallback(() => setPortraitLoaded(true), []);
  useEffect(() => { setPortraitLoaded(false); }, [simulation.characterState]);

  const currentNode = getNode(simulation.currentNodeId);
  const choices = currentNode?.isChoicePoint ? getChoices(simulation.currentNodeId) : [];

  // Handle terminal node (outcome calculation) — user-triggered via continue
  useEffect(() => {
    if (currentNode?.isTerminal) {
      const lastChoice = simulation.pathHistory[simulation.pathHistory.length - 1];
      const lastNode = getNode(lastChoice);
      calculateOutcome(lastNode?.isStrongChoice || false);
    }
  }, [currentNode, simulation.pathHistory, calculateOutcome]);

  // Advance from non-choice, non-terminal nodes (like S0_ENTRY -> S1_MA_OPEN)
  const handleContinue = () => {
    if (currentNode && !currentNode.isChoicePoint && !currentNode.isTerminal && currentNode.nextNode) {
      makeChoice({
        nodeId: currentNode.nextNode,
        meterDeltas: currentNode.meterDeltas,
        stage: getNode(currentNode.nextNode)?.stage,
      });
    }
  };

  // After viewing response, user clicks Continue to advance
  const handleResponseContinue = () => {
    if (lastResponse) {
      makeChoice({
        nodeId: lastResponse.nextNode,
        meterDeltas: lastResponse.meterDeltas,
        stage: getNode(lastResponse.nextNode)?.stage,
        characterState: lastResponse.characterState,
      });
      setShowResponse(false);
      setLastResponse(null);
    }
  };

  const handleChoiceHover = (choice) => {
    if (choice.meterDeltas) {
      setPreviewMeters(choice.meterDeltas);
    }
  };

  const handleChoiceLeave = () => {
    clearPreview();
  };

  const handleChoiceSelect = (choice) => {
    clearPreview();
    dismissNudge(); // Close any open coach nudge before evaluating the new choice

    // Check for intent mismatch
    const mismatches = checkIntentMismatch(choice, prepCard);
    
    mismatches.forEach(mismatch => {
      recordMismatch(mismatch.type, mismatch.trigger);
      
      if (mismatch.type === 'hard' || state.mismatches.soft.length === 0) {
        addCoachNudge({
          trigger: mismatch.trigger,
          stage: choice.stage,
        });
      }
    });

    // Update character portrait immediately so it reflects the mood of the response
    if (choice.resultingCharacterState) {
      updateCharacterState(choice.resultingCharacterState);
    }

    // Show the response — user must click Continue to advance
    setLastResponse({
      dialogue: choice.dialogue,
      expandedResponse: choice.expandedResponse,
      visualEvidence: choice.visualEvidence,
      nextNode: choice.nextNode,
      meterDeltas: choice.meterDeltas,
      characterState: choice.resultingCharacterState,
    });
    setShowResponse(true);
  };

  // Get quality indicator for choice
  const getQualityIndicator = (choice) => {
    if (choice.tags.includes(TAGS.STRONG)) {
      return { label: 'Strong', color: 'text-[#78BE20]' };
    }
    if (choice.tags.includes(TAGS.WEAK)) {
      return { label: 'Risky', color: 'text-[#DA291C]' };
    }
    if (choice.tags.includes(TAGS.MIXED)) {
      return { label: 'Mixed', color: 'text-[#FFB81C]' };
    }
    return null;
  };

  // Determine if we're on a non-choice, non-terminal node (needs Continue)
  const isNarrativeNode = currentNode && !currentNode.isChoicePoint && !currentNode.isTerminal && currentNode.nextNode;

  if (!currentNode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-slate-400">Loading simulation...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="simulation-view">
      {/* Fixed Top Bar for Meters */}
      <FixedTopBar />
      
      {/* Coach Nudge Overlay */}
      <CoachNudge />

      {/* Add top padding to account for fixed bar */}
      <div className="pt-14">
        <div className="grid grid-cols-12 min-h-[calc(100vh-56px)]">
          {/* Main Dialogue Area */}
          <div className="col-span-12 lg:col-span-9 p-4 sm:p-6 lg:p-12">
            {/* Stage Label */}
            <div className="mb-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0055A5] mb-1">
                <span>Rehearse</span>
                <span className="mx-2 text-slate-300">|</span>
                <span>{STAGE_LABELS[simulation.currentStage]}</span>
              </p>
            </div>

            {/* Character Portrait & Dialogue — side-by-side editorial layout */}
            <div className="relative mb-8">
              <div className="flex flex-col lg:flex-row">
                {/* Portrait — large, full height on desktop; shorter on mobile */}
                <motion.div
                  key={simulation.characterState}
                  initial={{ opacity: 0.6 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  className="w-full lg:w-[45%] flex-shrink-0 bg-[#E2E8F0] overflow-hidden"
                  style={{ minHeight: '200px' }}
                >
                  <img
                    src={getCharacterImage(simulation.characterState)}
                    alt="Ms. Ortega"
                    onLoad={handlePortraitLoad}
                    className="w-full h-48 lg:h-full object-cover object-top transition-opacity duration-500"
                    style={{ minHeight: '200px', opacity: portraitLoaded ? 1 : 0 }}
                    data-testid="character-portrait"
                  />
                </motion.div>

                {/* Dialogue panel — overlaps on desktop, stacks on mobile */}
                <div className="-mt-6 lg:-mt-0 lg:-ml-12 flex-1 z-10 flex flex-col justify-center py-3 lg:py-6 px-2 lg:px-0">
                  <AnimatePresence mode="wait">
                    {showResponse ? (
                      <motion.div
                        key="response"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                        className="space-y-3"
                        data-testid="response-display"
                      >
                        {/* What you said */}
                        <div className="p-4 bg-slate-100/95 backdrop-blur-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <User className="w-4 h-4 text-[#0055A5]" strokeWidth={1.5} />
                            <span className="text-xs font-semibold uppercase tracking-wider text-[#0055A5]">
                              You said
                            </span>
                          </div>
                          <p className="text-sm text-[#002B45] italic">
                            {lastResponse?.dialogue}
                          </p>
                        </div>

                        {/* Ms. Ortega responds */}
                        <div className="p-6 bg-white/95 backdrop-blur-sm shadow-ambient">
                          <div className="flex items-center gap-2 mb-3">
                            <MessageCircle className="w-4 h-4 text-[#002B45]" strokeWidth={1.5} />
                            <span className="text-xs font-semibold uppercase tracking-wider text-[#002B45]">
                              Ms. Ortega responds
                            </span>
                          </div>
                          <p className="text-base font-headline text-[#002B45] leading-relaxed">
                            {lastResponse?.expandedResponse}
                          </p>
                        </div>

                        {/* Continue button */}
                        <button
                          onClick={handleResponseContinue}
                          className="flex items-center gap-2 px-5 py-3 bg-[#0055A5] text-white text-sm font-medium hover:bg-[#002B45] transition-colors"
                          data-testid="continue-after-response"
                        >
                          Continue
                          <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div
                        key={currentNode.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                        data-testid="current-dialogue"
                      >
                        <div className="p-6 bg-white/95 backdrop-blur-sm shadow-ambient">
                          {currentNode.speaker === 'ortega' && (
                            <div className="flex items-center gap-2 mb-3">
                              <MessageCircle className="w-4 h-4 text-[#002B45]" strokeWidth={1.5} />
                              <span className="text-xs font-semibold uppercase tracking-wider text-[#002B45]">
                                Ms. Ortega
                              </span>
                            </div>
                          )}
                          {currentNode.speaker === 'system' && (
                            <div className="flex items-center gap-2 mb-3">
                              <AlertTriangle className="w-4 h-4 text-[#FFB81C]" strokeWidth={1.5} />
                              <span className="text-xs font-semibold uppercase tracking-wider text-[#FFB81C]">
                                Situation
                              </span>
                            </div>
                          )}
                          <p className="text-base lg:text-lg font-headline text-[#002B45] leading-relaxed">
                            {currentNode.dialogue}
                          </p>
                        </div>

                        {/* Continue button for narrative/system nodes */}
                        {isNarrativeNode && (
                          <button
                            onClick={handleContinue}
                            className="mt-3 flex items-center gap-2 px-5 py-3 bg-[#0055A5] text-white text-sm font-medium hover:bg-[#002B45] transition-colors"
                            data-testid="continue-narrative"
                          >
                            Continue
                            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* Choice Options — below the portrait area */}
            {choices.length > 0 && !showResponse && (
              <motion.div
                initial="hidden"
                animate="visible"
                variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
                className="space-y-3"
                data-testid="dialogue-choices"
              >
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">
                  Choose your response
                </p>
                
                {choices.map((choice, index) => {
                  const quality = getQualityIndicator(choice);
                  
                  return (
                    <motion.button
                      key={choice.id}
                      variants={{
                        hidden: { opacity: 0, y: 12 },
                        visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] } },
                      }}
                      onClick={() => handleChoiceSelect(choice)}
                      onMouseEnter={() => handleChoiceHover(choice)}
                      onMouseLeave={handleChoiceLeave}
                      className="dialogue-choice w-full p-5 text-left bg-white hover:bg-slate-50 shadow-ambient hover:shadow-ambient-strong transition-all group"
                      data-testid={`choice-${index}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-base text-[#002B45] font-headline leading-relaxed group-hover:text-[#0055A5] transition-colors">
                            {choice.dialogue}
                          </p>
                        </div>
                        
                        {quality && (
                          <span className={`text-[10px] font-semibold uppercase tracking-wider ${quality.color} whitespace-nowrap pt-1`}>
                            {quality.label}
                          </span>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </div>

          {/* Right Sidebar - Context Panel */}
          <div className="col-span-12 lg:col-span-3 bg-[#F1F5F9] p-4 sm:p-6 lg:p-8 space-y-6">
            {/* Prep Card Summary */}
            <PrepCardSummary />
            
            {/* Coach History */}
            <div className="bg-white shadow-ambient">
              <CoachNudgeHistory />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Simulation;
